import {
  Typography,
  Box,
  List,
  ListItem,
  makeStyles,
  IconButton,
  Collapse,
  Paper,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
} from "@material-ui/core";
import { GetApp, Print as PrintIcon } from "@material-ui/icons";
import { useState } from "react";
import { ExpandMore, ExpandLess } from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  processingBox: {
    marginTop: theme.spacing(2),
    textAlign: "center",
    padding: theme.spacing(3),
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    marginLeft: theme.spacing(1),
  },
  fileName: {
    marginLeft: theme.spacing(1),
    flexGrow: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resultContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  resultHeader: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    textAlign: "left",
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: theme.spacing(1),
  },
  resultSubHeader: {
    fontWeight: "bold",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textAlign: "left",
  },
  resultItem: {
    marginBottom: theme.spacing(1),
    textAlign: "left",
  },
  resultList: {
    textAlign: "left",
    paddingTop: theme.spacing(1),
  },
  listBullet: {
    display: "inline-block",
    // marginRight: theme.spacing(0.5),
    fontSize: "12px",
    color: theme.palette.primary.main,
  },
}));

const getDisplayLabel = (key, value) => {
  // Se o valor for um objeto e tiver um campo 'label', use-o
  if (value && typeof value === "object" && !Array.isArray(value)) {
    if (value.label) {
      return value.label;
    }

    // Verificar se o próprio valor tem uma key especial 'label' no nível superior
    // (pode ocorrer em templates específicos da API)
    if (
      Object.keys(value).includes("label") &&
      typeof value.label === "string"
    ) {
      return value.label;
    }
  }

  // Tentativa de formatar o nome da chave para exibição mais amigável
  // Transforma snake_case ou camelCase em palavras separadas com primeira letra maiúscula
  const formatted = key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^\w/, (c) => c.toUpperCase());

  return formatted;
};

const resultHTML = ({ result, showRawResult, rawResult, t }) => {
  const printResults = () => {
    alert(t('startingPrint'));
    if (!result) return;

    try {
      // Determinar qual resultado usar baseado na seleção do usuário
      const dataToPrint = showRawResult && rawResult ? rawResult : result;

      // Criamos um novo documento para impressão
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        alert(t('allowPopups'));
        return;
      }

      // Criamos um novo conteúdo HTML para impressão, apenas com os resultados
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>eLaw GenAI - ${
            showRawResult && rawResult ? t('rawResult') : t('finalResult')
          }</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.5;
              padding: 20px;
              color: #333;
            }
            h1 {
              color: #1976d2;
              font-size: 24px;
              margin-bottom: 16px;
              padding-bottom: 8px;
              border-bottom: 2px solid #e0e0e0;
            }
            h2 {
              color: #333;
              font-size: 18px;
              margin-top: 16px;
              margin-bottom: 8px;
            }
            h3 {
              color: #555;
              font-size: 16px;
              margin-top: 12px;
              margin-bottom: 6px;
            }
            .result-item {
              margin-bottom: 12px;
            }
            .list-item {
              margin-bottom: 4px;
              margin-left: 24px;
              position: relative;
            }
            .list-item:before {
              content: "•";
              color: #1976d2;
              position: absolute;
              left: -12px;
            }
            .empty-list {
              font-style: italic;
              color: #777;
              margin-left: 12px;
            }
            .item-label {
              font-weight: bold;
            }
            .item-value {
              margin-left: 8px;
            }
            .parent-item {
              margin-bottom: 16px;
              border-left: 1px solid #e0e0e0;
              padding-left: 12px;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <h1>eLaw GenAI - ${
            showRawResult && rawResult ? t('rawResult') : t('finalResult')
          }</h1>
          <div id="print-content">${renderPrintContent(dataToPrint)}</div>
        </body>
        </html>
      `;

      // Escrevemos o conteúdo na nova janela
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      // Aguardamos o conteúdo ser carregado e impressão ser chamada
      printWindow.onload = function () {
        printWindow.print();
        // Fechar a janela após a impressão (opcional - em alguns navegadores o usuário decide)
        printWindow.onafterprint = function () {
          printWindow.close();
        };
      };
    } catch (error) {
      console.error("Erro ao imprimir resultados:", error);
      alert(
        t('printError')
      );
    }
  };

  // Função auxiliar para gerar o HTML para impressão
  const renderPrintContent = (data) => {
    if (!data || typeof data !== "object") {
      return `<div class="empty-list">${t('noResultsAvailable')}</div>`;
    }

    let html = "";

    Object.entries(data).forEach(([key, value]) => {
      html += renderPrintItem(key, value, 0);
    });

    return html;
  };

  // Função recursiva para renderizar itens para impressão
  const renderPrintItem = (key, value, depth = 0) => {
    // Obter o label para exibição
    const displayLabel = getDisplayLabel(key, value);
    let html = "";

    // Renderizar o item baseado no tipo
    if (value === null || value === undefined) {
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2><div><em>Não definido</em></div>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span> <em>Não definido</em></div>`;
      }
      html += "</div>";
    } else if (typeof value === "object" && !Array.isArray(value)) {
      // É um objeto
      const metadataFields = ["label", "type", "description"];
      const keys = Object.keys(value);
      const hasOnlyMetadata = keys.every((k) => metadataFields.includes(k));

      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;

      if (depth === 0) {
        html += `<h2>${displayLabel}</h2>`;

        // Mostrar metadados se disponíveis
        if (value.description) {
          html += `<div style="margin-top: -8px; color: #555;">${value.description}</div>`;
        } else if (value.type) {
          html += `<div style="margin-top: -8px; color: #555; font-style: italic;">Tipo: ${value.type}</div>`;
        }
      } else {
        html += `<div><span class="item-label">${displayLabel}</span></div>`;
      }

      if (!hasOnlyMetadata) {
        html += '<div class="parent-item">';
        Object.entries(value)
          .filter(
            ([childKey]) =>
              !metadataFields.includes(childKey) || keys.length === 1
          )
          .forEach(([childKey, childValue]) => {
            html += renderPrintItem(childKey, childValue, depth + 1);
          });
        html += "</div>";
      }

      html += "</div>";
    } else if (Array.isArray(value)) {
      // É um array
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;

      if (depth === 0) {
        html += `<h2>${displayLabel}</h2>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span></div>`;
      }

      if (value.length === 0) {
        // Array vazio - mostrar mensagem
        html += '<div class="empty-list">Nenhum item encontrado</div>';
      } else {
        // Renderizar itens do array
        value.forEach((item, index) => {
          if (typeof item === "object" && item !== null) {
            html += '<div class="list-item">';

            // Se o item tiver um label, usar como título
            if (item.label) {
              html += `<div style="font-weight: bold;">${item.label}</div>`;
            }

            // Renderizar os campos do objeto dentro do item da lista
            Object.entries(item)
              .filter(
                ([itemKey]) =>
                  itemKey !== "label" &&
                  itemKey !== "type" &&
                  itemKey !== "description"
              )
              .forEach(([itemKey, itemValue]) => {
                html += renderPrintItem(itemKey, itemValue, depth + 2);
              });

            html += "</div>";
          } else {
            html += `<div class="list-item">${String(item)}</div>`;
          }
        });
      }

      html += "</div>";
    } else {
      // É um valor primitivo
      html += `<div class="result-item" style="margin-left: ${depth * 16}px">`;
      if (depth === 0) {
        html += `<h2>${displayLabel}</h2><div>${String(value)}</div>`;
      } else {
        html += `<div><span class="item-label">${displayLabel}:</span> <span class="item-value">${String(
          value
        )}</span></div>`;
      }
      html += "</div>";
    }

    return html;
  };

  return printResults;
};

// Função recursiva para renderizar os resultados em formato adequado
const RenderResultItem = ({
  keyName,
  value,
  depth = 0,
  path = "",
  showEmptyItems,
}) => {
  const classes = useStyles();
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const isNodeExpanded = (nodePath) => {
    // Se o nó não está no Set, significa que ainda não foi clicado
    // Nesse caso, retornamos true para que todos comecem expandidos
    return !expandedNodes.has(nodePath);
  };

  const toggleNode = (nodePath) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodePath)) {
        newSet.delete(nodePath);
      } else {
        newSet.add(nodePath);
      }
      return newSet;
    });
  };

  const nodePath = path ? `${path}.${keyName}` : keyName;
  const isExpanded = isNodeExpanded(nodePath);

  // Obter o label para exibição
  const displayLabel = getDisplayLabel(keyName, value);

  // Definir o espaçamento para níveis hierárquicos
  const indentSize = depth * 16; // Mantido em 16px por nível para não ficar excessivo

  // Função auxiliar para renderizar o título do item com estilo consistente
  const renderItemTitle = (showValue = null) => {
    if (depth === 0) {
      // Estilo destacado para todos os itens de nível raiz
      return (
        <>
          <Typography
            variant="h6"
            className={classes.resultHeader}
            align="left"
          >
            {displayLabel}
          </Typography>
          {showValue !== null && (
            <Typography
              variant="body1"
              style={{
                marginTop: -4,
                marginBottom: 12,
                paddingLeft: 8,
                color: "#555",
                fontStyle:
                  showValue.startsWith("Tipo:") ||
                  showValue.startsWith("Lista com")
                    ? "italic"
                    : "normal",
              }}
            >
              {showValue}
            </Typography>
          )}
        </>
      );
    } else {
      // Estilo normal para os demais níveis
      return (
        <Typography
          variant="subtitle1"
          className={classes.resultSubHeader}
          style={{ textAlign: "left" }}
          align="left"
        >
          {displayLabel}
        </Typography>
      );
    }
  };

  // Função auxiliar para renderizar o conteúdo de um item
  const renderItemContent = (content) => {
    return (
      <Box
        ml={2}
        style={{
          textAlign: "left",
          borderLeft: "1px solid #e0e0e0",
          paddingLeft: 8,
        }}
      >
        {content}
      </Box>
    );
  };

  // Renderizar diferentes tipos de valores
  if (value === null || value === undefined) {
    // Se não devemos mostrar itens vazios e não é um item raiz, retornar null
    if (!showEmptyItems && depth > 0) {
      return null;
    }

    return (
      <Box
        key={keyName}
        className={classes.resultItem}
        style={{
          paddingLeft: indentSize,
          textAlign: "left",
          marginBottom: depth === 0 ? 24 : 8, // Margem inferior maior para itens raiz
        }}
      >
        {depth === 0 ? (
          renderItemTitle()
        ) : (
          <Typography variant="body2" align="left">
            <strong>{displayLabel}:</strong> <em>Não definido</em>
          </Typography>
        )}
      </Box>
    );
  } else if (typeof value === "object" && !Array.isArray(value)) {
    // É um objeto
    // Determinar quais campos não mostrar diretamente (campos de metadados)
    const metadataFields = ["label", "type", "description"];

    // Se for um objeto que tem apenas os campos de metadados, mostrar diferente
    const keys = Object.keys(value);
    const hasOnlyMetadata = keys.every((k) => metadataFields.includes(k));

    if (hasOnlyMetadata && keys.length > 0) {
      // Mostrar os metadados como uma descrição
      let description = "";
      if (value.description) {
        description = value.description;
      } else if (value.type) {
        description = `Tipo: ${value.type}`;
      }

      return (
        <Box
          key={keyName}
          className={classes.resultItem}
          style={{
            paddingLeft: indentSize,
            textAlign: "left",
            marginBottom: depth === 0 ? 24 : 8, // Margem inferior maior para itens raiz
          }}
        >
          {depth === 0 ? (
            renderItemTitle(description ? description : null)
          ) : (
            <Typography variant="body2" align="left">
              <strong>{displayLabel}:</strong>
              {description && (
                <em style={{ marginLeft: 8, color: "gray" }}>{description}</em>
              )}
            </Typography>
          )}
        </Box>
      );
    }

    // Se não é um objeto que tem apenas metadados, renderizar normalmente com filhos
    // Vamos verificar se tem algum metadado importante para mostrar
    let metadataInfo = "";
    if (value.type) {
      metadataInfo = `Tipo: ${value.type}`;
    }
    if (value.description) {
      metadataInfo = metadataInfo
        ? `${metadataInfo}, ${value.description}`
        : value.description;
    }

    // Filtrar os filhos para remover itens vazios se necessário
    const filteredEntries = Object.entries(value)
      .filter(
        ([childKey]) => !metadataFields.includes(childKey) || keys.length === 1
      )
      .map(([childKey, childValue]) => (
        <RenderResultItem
          keyName={childKey}
          value={childValue}
          depth={depth + 1}
          path={nodePath}
          showEmptyItems={showEmptyItems}
        />
      ))
      .filter(Boolean); // Remove nulls (itens vazios filtrados)

    // Se não temos filhos para mostrar e não devemos mostrar itens vazios, retornar null
    if (!showEmptyItems && filteredEntries.length === 0 && depth > 0) {
      return null;
    }

    return (
      <Box
        key={keyName}
        style={{
          paddingLeft: indentSize,
          textAlign: "left",
          marginBottom: depth === 0 ? 24 : 8,
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => toggleNode(nodePath)}
            style={{ marginRight: 4, padding: 4 }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {depth === 0 ? (
            renderItemTitle(metadataInfo ? metadataInfo : null)
          ) : (
            <Typography
              variant="subtitle1"
              style={{ textAlign: "left" }}
              align="left"
            >
              {displayLabel}
            </Typography>
          )}
        </Box>
        <Collapse in={isExpanded}>
          {renderItemContent(filteredEntries)}
        </Collapse>
      </Box>
    );
  } else if (Array.isArray(value)) {
    // É um array
    // Filtrar itens vazios se necessário
    const filteredItems =
      value.length > 0
        ? value
            .map((item, index) => {
              if (typeof item === "object" && item !== null) {
                const renderedItem = (
                  <ListItem
                    key={index}
                    dense
                    disableGutters
                    style={{ textAlign: "left", marginBottom: 8 }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      style={{ textAlign: "left", width: "100%" }}
                    >
                      <span
                        className={classes.listBullet}
                        style={{
                          color: "#1976d2",
                          marginRight: 4,
                          marginTop: 3,
                        }}
                      >
                        ●
                      </span>
                      <Box style={{ textAlign: "left", width: "100%" }}>
                        {/* Se o item tiver um campo label, usar como título do item */}
                        {item.label && (
                          <Typography
                            variant="subtitle2"
                            style={{ fontWeight: "bold", marginBottom: 4 }}
                          >
                            {item.label}
                          </Typography>
                        )}

                        {/* Filtrar os campos de metadados */}
                        {Object.entries(item)
                          .filter(
                            ([itemKey]) =>
                              itemKey !== "label" &&
                              itemKey !== "type" &&
                              itemKey !== "description"
                          )
                          .map(([itemKey, itemValue]) => (
                            <RenderResultItem
                              keyName={itemKey}
                              value={itemValue}
                              depth={depth + 2}
                              path={`${nodePath}[${index}]`}
                              showEmptyItems={showEmptyItems}
                            />
                          ))}
                      </Box>
                    </Box>
                  </ListItem>
                );

                // Se não devemos mostrar itens vazios, verificar se o item tem conteúdo
                if (!showEmptyItems) {
                  const hasContent = Object.entries(item)
                    .filter(
                      ([itemKey]) =>
                        itemKey !== "label" &&
                        itemKey !== "type" &&
                        itemKey !== "description"
                    )
                    .some(([_, itemValue]) => {
                      if (itemValue === null || itemValue === undefined)
                        return false;
                      if (
                        typeof itemValue === "object" &&
                        !Array.isArray(itemValue)
                      ) {
                        return Object.keys(itemValue).length > 0;
                      }
                      if (Array.isArray(itemValue)) {
                        return itemValue.length > 0;
                      }
                      return true;
                    });

                  return hasContent ? renderedItem : null;
                }

                return renderedItem;
              } else {
                return (
                  <ListItem
                    key={index}
                    dense
                    disableGutters
                    style={{ textAlign: "left" }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      style={{ textAlign: "left", width: "100%" }}
                    >
                      <span
                        className={classes.listBullet}
                        style={{
                          color: "#1976d2",
                          marginRight: 4,
                          marginTop: 3,
                        }}
                      >
                        ●
                      </span>
                      <Typography variant="body2" align="left">
                        {String(item)}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              }
            })
            .filter(Boolean)
        : null;

    // Se não temos itens para mostrar e não devemos mostrar itens vazios, retornar null
    if (
      !showEmptyItems &&
      (!filteredItems || filteredItems.length === 0) &&
      depth > 0
    ) {
      return null;
    }

    return (
      <Box
        key={keyName}
        style={{
          paddingLeft: indentSize,
          textAlign: "left",
          marginBottom: depth === 0 ? 24 : 8,
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={() => toggleNode(nodePath)}
            style={{ marginRight: 4, padding: 4 }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {depth === 0 ? (
            renderItemTitle(null)
          ) : (
            <Typography
              variant="subtitle1"
              style={{ textAlign: "left" }}
              align="left"
            >
              <strong>{displayLabel}:</strong>
            </Typography>
          )}
        </Box>
        <Collapse in={isExpanded}>
          <List
            dense
            className={classes.resultList}
            style={{
              textAlign: "left",
              borderLeft: "1px solid #e0e0e0",
              marginLeft: 8,
              paddingLeft: 8,
            }}
          >
            {filteredItems ? (
              filteredItems
            ) : (
              <ListItem dense disableGutters style={{ textAlign: "left" }}>
                <Typography
                  variant="body2"
                  style={{ fontStyle: "italic", color: "#666" }}
                >
                  Nenhum item encontrado
                </Typography>
              </ListItem>
            )}
          </List>
        </Collapse>
      </Box>
    );
  } else {
    // É um valor primitivo (string, número, booleano)
    return (
      <Box
        key={keyName}
        className={classes.resultItem}
        style={{
          paddingLeft: indentSize,
          textAlign: "left",
          marginBottom: depth === 0 ? 24 : 8, // Margem inferior maior para itens raiz
        }}
      >
        {depth === 0 ? (
          renderItemTitle(String(value))
        ) : (
          <Typography variant="body2" align="left">
            <strong>{displayLabel}:</strong> {String(value)}
          </Typography>
        )}
      </Box>
    );
  }
};

// Função para baixar os resultados como JSON

const BoxRenderResult = ({ result, rawResult = null }) => {
  const classes = useStyles();
  const [showRawResult, setShowRawResult] = useState(false);
  const [showEmptyItems, setShowEmptyItems] = useState(false);
  const { t } = useTranslation('editor');

  const downloadResultsAsJson = () => {
    if (!result) return;

    try {
      // Determinar qual resultado usar baseado na seleção do usuário
      const dataToDownload = showRawResult && rawResult ? rawResult : result;

      // Criar uma string JSON formatada
      const jsonString = JSON.stringify(dataToDownload, null, 2);

      // Criar um blob com o conteúdo JSON
      const blob = new Blob([jsonString], { type: "application/json" });

      // Criar uma URL temporária para o blob
      const url = URL.createObjectURL(blob);

      // Criar um elemento de link para o download
      const link = document.createElement("a");
      link.href = url;
      link.download =
        showRawResult && rawResult ? "resultado-bruto.json" : "resultado.json";

      // Adicionar o link ao documento, clicar nele e removê-lo
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Liberar a URL do objeto
      URL.revokeObjectURL(url);

      console.log(t('downloadStarted'));
    } catch (error) {
      console.error("Erro ao baixar resultados como JSON:", error);
              alert(t('jsonError'));
    }
  };

  return (
    <Paper className={classes.resultContainer}>
      <Typography variant="h6" gutterBottom align="left">
        <Box
          fontWeight="fontWeightBold"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <span>{t('processingResult')}</span>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showEmptyItems}
                  onChange={(e) => setShowEmptyItems(e.target.checked)}
                  color="primary"
                />
              }
              label={t('showEmptyFields')}
              style={{ marginRight: 16 }}
            />
            {rawResult && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showRawResult}
                    onChange={(e) => setShowRawResult(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('showRawResult')}
                style={{ marginRight: 16 }}
              />
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={resultHTML({ result, showRawResult, rawResult, t })}
              size="small"
              style={{ marginRight: 8 }}
            >
              {t('printResult')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetApp />}
              onClick={downloadResultsAsJson}
              size="small"
            >
              {t('saveResultJson')}
            </Button>
          </Box>
        </Box>
      </Typography>
      <Divider style={{ marginBottom: 16 }} />

      <Box style={{ textAlign: "left" }}>
        {Object.entries(showRawResult && rawResult ? rawResult : result).map(
          ([key, value]) => (
            <RenderResultItem
              keyName={key}
              value={value}
              showEmptyItems={showEmptyItems}
            />
          )
        )}
      </Box>
    </Paper>
  );
};

export { RenderResultItem, BoxRenderResult };
